/**
 * Der Arbeitsfluss eines Monats als sichtbare Kette.
 *
 * Excel verlangte, die Reihenfolge im Kopf zu haben. Hier steht sie auf dem
 * Bildschirm, mit genau einem hervorgehobenen nächsten Schritt. Die fachlichen
 * Schutzregeln ändert das nicht — es geht nur um die Orientierung davor.
 *
 * Ist eine Hälfte abgeschlossen, steht hier die nächste: „Neue Abrechnung
 * starten“, darüber eine Zeile, dass die alte erledigt ist. Läuft die nächste
 * noch, drängt nichts. Vorher blieb die Karte auf der fertigen Hälfte stehen
 * und meldete „alles erledigt“, während die nächste längst fällig war.
 */
import { Link } from "react-router-dom";
import type { PeriodDto, WorkflowDto, WorkflowStepDto } from "../lib/api";
import { euro } from "../lib/format";

export function Workflow({ workflow }: { workflow: WorkflowDto }) {
  const { periode, stand, vorige } = workflow;
  const aktiv = workflow.schritte.find((s) => s.status === "aktiv");

  return (
    <section className="card mb-10 p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="label">{stand === "neu" ? "Nächste Abrechnung" : "Abrechnungszeitraum"}</div>
          <h2 className="text-lg">{periode?.label ?? "Noch keine Daten"}</h2>
          {periode ? (
            <p className="text-xs text-muted">
              {datum(periode.from)} bis {datum(periode.to)} ·{" "}
              {stand === "laeuft_noch"
                ? "läuft noch"
                : periode.tage === 0
                  ? "noch keine Daten"
                  : `${periode.tage} ${periode.tage === 1 ? "Betriebstag" : "Betriebstage"}`}
            </p>
          ) : null}
        </div>
        {stand === "neu" ? (
          <Link to="/import" className="btn-recommend">
            Neue Abrechnung starten
          </Link>
        ) : stand === "laeuft_noch" ? (
          <Link to="/import" className="btn-ghost" title="Geht auch schon jetzt, nötig ist es erst nach dem Ende der Hälfte">
            Dateien einlesen
          </Link>
        ) : aktiv ? (
          <Link to={aktiv.ziel} className="btn-recommend">
            {aktiv.titel}
          </Link>
        ) : (
          <span className="chip-ok">Zeitraum abgeschlossen</span>
        )}
      </div>

      {vorige ? <VorigeHaelfte periode={vorige} /> : null}
      {stand === "laeuft_noch" && periode ? (
        <p className="mb-4 text-sm text-muted">
          Bis zum {datum(periode.to).slice(0, 6)} läuft die Hälfte noch. Danach die drei Dateien
          einlesen, dann geht es hier weiter.
        </p>
      ) : null}

      <ol className="flex flex-wrap gap-x-1 gap-y-3">
        {workflow.schritte.map((schritt, i) => (
          <li key={schritt.id} className="flex items-stretch">
            <Step schritt={schritt} />
            {i < workflow.schritte.length - 1 ? (
              <span className="self-center px-1 text-muted" aria-hidden>
                ›
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Dass die alte Hälfte erledigt ist, mit Summe — und, bis sie erzeugt ist,
 * der Hinweis auf die Überweisungsdatei. Er hält die neue Hälfte nicht auf:
 * im Parallellauf wird womöglich noch über SEPAApp überwiesen.
 */
function VorigeHaelfte({ periode }: { periode: PeriodDto }) {
  const name = periode.label.charAt(0).toUpperCase() + periode.label.slice(1);
  if (periode.istAltbestand) {
    return <p className="mb-4 text-sm text-ok">✓ {name} aus Excel übernommen</p>;
  }
  return (
    <p className="mb-4 flex flex-wrap items-baseline gap-x-2 text-sm">
      <span className="text-ok">✓ {name} abgeschlossen</span>
      <span className="text-muted">
        · {periode.tage} {periode.tage === 1 ? "Tag" : "Tage"}
        {periode.trinkgeld ? ` · ${euro(periode.trinkgeld)}` : ""}
      </span>
      {periode.auszahlungStatus === "PAID" ? (
        <span className="text-muted">· ausgezahlt</span>
      ) : periode.exportiertAm ? (
        <span className="text-muted">· Überweisungsdatei am {tagAm(periode.exportiertAm)} erzeugt</span>
      ) : (
        <Link
          to={periode.auszahlungId ? `/auszahlungen?periode=${periode.auszahlungId}` : "/auszahlungen"}
          className="text-warn hover:underline"
        >
          · Überweisungsdatei noch nicht erzeugt →
        </Link>
      )}
    </p>
  );
}

function Step({ schritt }: { schritt: WorkflowStepDto }) {
  const ton =
    schritt.status === "fertig"
      ? "border-ok/30 bg-ok/5 text-ok"
      : schritt.status === "aktiv"
        ? "border-accent bg-accent/5 text-ink"
        : "border-line bg-page text-muted";

  const inhalt = (
    <>
      <span className="flex items-center gap-1.5 font-mono text-label uppercase">
        {schritt.status === "fertig" ? <span aria-hidden>✓</span> : null}
        {schritt.titel}
      </span>
      <span className="mt-1 block text-xs">{schritt.text}</span>
    </>
  );

  const klasse = `block rounded-sm border px-3 py-2 ${ton}`;

  // Ein wartender Schritt ist kein Ziel: er führt nur ins Leere und nimmt dem
  // aktiven Schritt die Aufmerksamkeit.
  return schritt.status === "wartet" ? (
    <span className={klasse}>{inhalt}</span>
  ) : (
    <Link to={schritt.ziel} className={`${klasse} transition-colors hover:border-ink`}>
      {inhalt}
    </Link>
  );
}

/** `2026-09-16` → `16.09.2026` */
function datum(iso: string): string {
  return iso.split("-").reverse().join(".");
}

/** Zeitpunkt aus der API → `03.10.`, in der Ortszeit des Browsers. */
function tagAm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
}
